import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/redux';
import Sidebar from './Sidebar';
import FeedbackModal from './FeedbackModal';
import EutcAcceptanceGate from './EutcAcceptanceGate';
import ForcePasswordChangeDialog from './ForcePasswordChangeDialog';
import { apiService } from '../services/apiService';

const drawerWidth = 260;

interface MainLayoutProps {
  children: React.ReactNode;
  title: string;
  // The onboarding wizard and seeded-review screens want a focused,
  // full-width surface with no nav. This drops just the Sidebar (and its
  // mobile toggle).
  hideSidebar?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, title, hideSidebar = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleFeedbackSubmit = async (data: { title: string; description: string; files: File[] }) => {
    try {
      setFeedbackSubmitting(true);
      setFeedbackError(null);

      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);

      data.files.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await apiService.postFormData('/admin/feedback', formData);

      if (response?.success) {
        setShowFeedbackModal(false);
        setSnackbar({
          open: true,
          message: 'Thank you for your feedback!',
          severity: 'success'
        });
      } else {
        setFeedbackError(response?.message || 'Failed to submit feedback');
      }
    } catch (err: any) {
      console.error('Failed to submit feedback:', err);
      setFeedbackError(err.message || 'Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile menu button */}
      {!hideSidebar && (
        <IconButton
          aria-label="Open navigation menu"
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 1200,
            display: { md: 'none' },
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: 'grey.100',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {!hideSidebar && (
        <Sidebar
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          userEmail={user?.email ?? undefined}
          onFeedbackClick={() => setShowFeedbackModal(true)}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 1,
          pl: { xs: 2, md: hideSidebar ? 2 : 0 },
          pr: 2,
          pb: 2,
          width: { md: hideSidebar ? '100%' : `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {children}
      </Box>

      {/* Blocking post-login gates: EUTC acceptance first, then forced password change */}
      <EutcAcceptanceGate />
      <ForcePasswordChangeDialog />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackError(null);
        }}
        onSubmit={handleFeedbackSubmit}
        submitting={feedbackSubmitting}
        error={feedbackError}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout;
